/* eslint-disable max-classes-per-file */
import { Component, registerComponent } from 'js/component.js';
import { scrollToAnchor } from 'js/ra.js';
import { getBreakpointMediaQuery } from 'js/constants.js';

const componentName = 'binge-experience';
const bingeExperienceIframeClass = `${componentName}__iframe`; // Referenced from ra.js

const disableBackgroundScrolling = () => {
    document.body.classList.add('body-locked'); // Prevent scrolling on underlying page
    if (document.querySelector('ra-header')) {
        document.querySelector('ra-header').style.display = 'none';
    }
};

const enableBackgroundScrolling = () => {
    document.body.classList.remove('body-locked'); // Prevent scrolling on underlying page
    if (document.querySelector('ra-header')) {
        document.querySelector('ra-header').style.display = '';
    }
};

function openPlexModal(modalId) {
    if (!modalId) {
        return null;
    }
    const modalEl = document.querySelector(`dialog${modalId}`);
    if (!modalEl) {
        return null;
    }
    const formEl = modalEl.querySelector('form');
    if (formEl && !formEl.dataset.bingePatched) {
        formEl.dataset.bingePatched = 'true';
        formEl.setAttribute('data-redirect', window.location.href);
        formEl.addEventListener('submit', () => {
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent('binge-gate-submitted'));
                modalEl.close();
            }, 2000);
        }, { once: true });
    }
    modalEl.showModal();
    return modalEl;
}

export class BingeExperience extends Component {

    // [LEGACY BREAKPOINT: max-width 48em -> getBreakpointMediaQuery('down', 'small')]
    matchMobile = getBreakpointMediaQuery('down', 'small');

    // ==========
    // COMPONENT SETUP
    // ==========
    selectors = {
        card: `.${componentName}__card`,
        cardDetail: `.${componentName}__card-detail`,
        videoTemplate: `.${componentName}__video-template`,
    };

    elements = {
        // Mobile teaser
        mobileTeaserButton: this.element.querySelector(`.${componentName}__mobile-teaser-button`),
        // Content
        container: this.element.querySelector(`.${componentName}__container`),
        mobileCloseButton: this.element.querySelector(`.${componentName}__mobile-close-button`),
        contentContainer: this.element.querySelector(`.${componentName}__content-container`),
        contentWrapper: this.element.querySelector(`.${componentName}__content-wrapper`),
        coverButton: this.element.querySelector(`.${componentName}__cover-button`),
        nextButton: this.element.querySelector(`.${componentName}__next-button`),
        // Drawer
        drawer: this.element.querySelector(`.${componentName}__drawer`),
        drawerArrowLeft: this.element.querySelector(`.${componentName}__drawer-arrow-left`),
        drawerArrowRight: this.element.querySelector(`.${componentName}__drawer-arrow-right`),
        drawerCardContainer: this.element.querySelector(`.${componentName}__drawer-card-container`),
        cards: this.element.querySelectorAll(this.selectors.card),
        // Buttons
        emailText: this.element.querySelector(`.${componentName}__email-text`),
        shareButtons: this.element.querySelectorAll('button[data-share]'),
    };

    formConfig = {
        conditionalGateId: this.elements.container.dataset.conditionalGateId, // gated form ID
        condition: this.elements.container.dataset.type, // 'content', 'time', or 'specific'
        totalTime: Number(this.elements.container.dataset.time), // serve after x total seconds
        assetCount: Number(this.elements.container.dataset.count), // serve after viewing x assets
        specificAsset: Number(this.elements.container.dataset.serveOnAsset) - 1, // serve on asset x (first asset is "1")
        specificAssetCondition: this.elements.container.dataset.serveBasedOnAsset, // 'time' or 'depth'
        specificAssetTime: Number(this.elements.container.dataset.timeViewed), // serve on asset x after y seconds
        specificAssetPages: Number(this.elements.container.dataset.scrollDepth), // serve on asset x after y pages
    };

    state = {
        wasFormTriggered: false,
        contentAssetsViewed: 0,
        currentPdfPage: 0,
        scrollHandler: null,
    };

    // ==========
    // CONTENT
    // ==========
    showInitialContent = () => {
        // Open content that matches initial hash
        const { hash } = window.location;
        if (hash) {
            const matchingCard = this.element.querySelector(`${this.selectors.card}[href="${hash}"]`);
            if (matchingCard) {
                if (this.matchMobile.matches) {
                    this.openMobileContainer();
                }
                this.selectCard(matchingCard);
                return;
            }
        }
        // Else if no cover is present, show first item
        if (!this.elements.coverButton) {
            const firstCard = this.elements.cards[0];
            firstCard.setAttribute('aria-current', true);
            this.showContent(firstCard);
        }
    };

    /**
     * @param {HTMLElement} element - The element with the data attributes describing the content item
     */
    showContent = async (element) => {
        const { type, url } = element.dataset;
        let contentEl;
        switch (type) {
        case 'page': {
            contentEl = document.createElement('iframe');
            contentEl.classList.add(bingeExperienceIframeClass);
            contentEl.src = url;
            break;
        }
        case 'pdf': {
            contentEl = await this.createPdfElement(url);
            break;
        }
        case 'image': {
            contentEl = document.createElement('img');
            contentEl.src = url;
            break;
        }
        case 'video': {
            const videoTemplate = element.querySelector(this.selectors.videoTemplate);
            contentEl = videoTemplate.content.cloneNode(true);
            break;
        }
        default:
            return;
        }
        this.elements.contentWrapper.replaceChildren(contentEl);

        this.elements.nextButton.hidden = !element.nextElementSibling; // Hide next button on last asset
        this.state.contentAssetsViewed += 1;
        this.checkFormConditions();
    };

    /**
     * @param {string} url
     * @returns {Promise<HTMLElement>}
     */
    createPdfElement = async (url) => {
        const pdfUrl = await this.checkPdfTranslation(url);
        const iframeEl = document.createElement('iframe');
        iframeEl.src = `/etc.clientlibs/rockwell-aem-base/components/content/pdf-viewer/clientlibs/site/resources/viewer.html?file=${pdfUrl}`;
        document.addEventListener('webviewerloaded', this.pdfPageHandler, { once: true }); // Attach page handler once web viewer is loaded
        return iframeEl;
    };

    /**
     * @param {string} url
     * @returns {Promise<string>}
     */
    checkPdfTranslation = async (url) => {
        const pageLanguage = document.querySelector('meta[property="language"]')?.content || 'en';
        const pageCountry = document.querySelector('meta[property="country"]')?.content || 'US';
        const pdfLanguage = pageCountry === 'TW' ? 'zc' : pageLanguage;
        if (pdfLanguage !== 'en') {
            const translatedUrl = url.replace(
                /^\/content?(.+-)([a-z]{2})(_[A-Z]{2})?(-.*?\.pdf.*)$/,
                `/content$1${pdfLanguage}$4`
            );
            const response = await fetch(translatedUrl); // TODO: Test if we can use HTTP 'HEAD' request instead here since response body is not needed
            if (response.ok) {
                return translatedUrl;
            }
        }
        return url;
    };

    // ==========
    // GATED FORM
    // ==========
    openGatedForm = () => {
        const plexModalId = this.elements.container.dataset.plexGateModalId;

        if (plexModalId) {
            if (openPlexModal(plexModalId)) {
                this.element.dispatchEvent(new CustomEvent(`${componentName}__form-triggered`));
            }
        } else if (this.formConfig.conditionalGateId) {
            const gatedContentName = window.GatedContentObject || 'gcdc';
            const gatedContentObject = window[gatedContentName];
            if (gatedContentObject) {
                const gateDiv = document.createElement('div');
                gateDiv.setAttribute('href', this.formConfig.conditionalGateId);
                document.body.appendChild(gateDiv);
                gatedContentObject('loadGates');
                this.element.dispatchEvent(new CustomEvent(`${componentName}__form-triggered`));
            }
        }
    };

    checkFormConditions = () => {
        if (!this.state.wasFormTriggered) {
            switch (this.formConfig.condition) {

            // Serve form after a number of assets have been viewed
            case 'content':
                if (this.state.contentAssetsViewed >= this.formConfig.assetCount) {
                    this.openGatedForm();
                    this.state.wasFormTriggered = true;
                }
                break;

            // Serve form after a number of seconds have elapsed
            case 'time':
                window.setTimeout(this.openGatedForm, this.formConfig.totalTime * 1000); // TODO: use intersection observer API
                this.state.wasFormTriggered = true;
                break;

            // Serve form when a condition on a specific asset is fulfilled
            case 'specific': {
                const selectedCardIndex = [...this.elements.cards].findIndex((card) => card.getAttribute('aria-current'));
                if (selectedCardIndex === this.formConfig.specificAsset) {
                    switch (this.formConfig.specificAssetCondition) {

                    // Serve form after a number of seconds have elapsed on a specific asset
                    case 'time':
                        window.setTimeout(this.openGatedForm, this.formConfig.specificAssetTime * 1000);
                        this.state.wasFormTriggered = true;
                        break;

                    // Serve form when a specific PDF reaches a specific page
                    case 'depth':
                        if (this.state.currentPdfPage >= this.formConfig.specificAssetPages) {
                            this.openGatedForm();
                            this.state.wasFormTriggered = true;
                        }
                        break;
                    default:
                        // do nothing
                        break;
                    }
                }
                break;
            }
            default:
                // do nothing
                break;
            }
        }
    };

    pdfPageHandler = () => {
        const iframeEl = this.elements.contentWrapper.firstElementChild;
        const { PDFViewerApplication } = iframeEl.contentWindow;
        PDFViewerApplication.initializedPromise.then(() => {
            PDFViewerApplication.eventBus.on('metadataloaded', (event) => {
                PDFViewerApplication.eventBus.on('pagechanging', () => {
                    this.state.currentPdfPage = event.source.page;
                    this.checkFormConditions();
                });
            });
        });
    };

    // ==========
    // UI LOGIC
    // ==========
    /**
     * @param {HTMLElement} selectedEl
     */
    selectCard = (selectedEl) => {
        if (selectedEl.getAttribute('aria-current')) { // Ignore if already selected
            return;
        }

        // Update aria-current
        this.elements.cards.forEach((card) => {
            card.removeAttribute('aria-current');
        });
        selectedEl.setAttribute('aria-current', true);

        this.handleCardBlur({ currentTarget: selectedEl }); // Remove hover card
        selectedEl.scrollIntoView({ block: 'nearest' }); // Scroll selected item into view
        this.showContent(selectedEl);

        if (document.readyState === 'complete') {
            scrollToAnchor(this.elements.container);
        } else {
            window.addEventListener('load', () => {
                scrollToAnchor(this.elements.container);
            });
        }

        this.element.dispatchEvent(new CustomEvent(`${componentName}__card-selected`, { detail: selectedEl.dataset }));
    };

    /**
     * @param {number} distance
     */
    scrollDrawer = (distance) => {
        this.elements.drawerCardContainer.scrollBy({ left: distance, behavior: 'smooth' });
    };

    openMobileContainer = () => {
        this.elements.container.setAttribute('open', '');
        disableBackgroundScrolling();
    };

    closeMobileContainer = () => {
        this.elements.container.removeAttribute('open');
        enableBackgroundScrolling();
    };

    // ==========
    // EVENT HANDLERS
    // ==========
    handleCardClick = (ev) => this.selectCard(ev.currentTarget);

    handleNextButtonClick = () => {
        const currentCard = this.element.querySelector(`${this.selectors.card}[aria-current]`);
        if (!currentCard) {
            this.selectCard(this.elements.cards[0]);
        } else if (currentCard.nextElementSibling) {
            this.selectCard(currentCard.nextElementSibling);
        }
    };

    /* Detail card uses "position: fixed" and bottom/left coordinates specified
        using javascript because drawerCardContainer is horizontally scrollable.
        Unfortunately you can not have visible overflow-y and scrollable
        overflow-x at the same time. */
    handleCardFocus = (ev) => {
        const cardEl = ev.currentTarget;
        if (cardEl.getAttribute('aria-current')) { // Do not display hover on selected card
            return;
        }

        let cardRect = cardEl.getBoundingClientRect();
        const containerRect = this.elements.drawerCardContainer.getBoundingClientRect();

        if (containerRect.left < cardRect.left && containerRect.right > cardRect.right) { // Check if the card is fully visible
            const detailEl = cardEl.querySelector(this.selectors.cardDetail);
            const detailOverflow = 10;
            detailEl.style.minHeight = `${cardRect.height + (detailOverflow * 2)}px`;
            detailEl.style.width = `${cardRect.width + (detailOverflow * 2)}px`;

            // Throttle scroll event: https://developer.mozilla.org/en-US/docs/Web/API/Element/scroll_event#scroll_event_throttling
            let ticking = false;
            this.state.scrollHandler = () => {
                cardRect = cardEl.getBoundingClientRect();
                if (!ticking) {
                    window.requestAnimationFrame(() => {
                        detailEl.style.bottom = `${window.innerHeight - cardRect.bottom - detailOverflow}px`;
                        detailEl.style.left = `${cardRect.left - detailOverflow}px`;
                        ticking = false;
                    });
                }
                ticking = true;
            };

            this.state.scrollHandler(); // Initialize detailEl position
            window.addEventListener('scroll', this.state.scrollHandler); // Update detailEl position on scroll

            // Reveal detailEl
            detailEl.style.visibility = 'visible';
            detailEl.style.opacity = 1;
        }
    };

    handleCardBlur = (ev) => {
        const detailEl = ev.currentTarget.querySelector(this.selectors.cardDetail);
        detailEl.style.visibility = null;
        detailEl.style.opacity = null;
        window.removeEventListener('scroll', this.state.scrollHandler);
    };

    handleShareButton = (ev) => {
        const cardEl = ev.currentTarget.closest(this.selectors.card);
        const { assetTitle, url } = cardEl.dataset;
        switch (ev.currentTarget.dataset.share) {
        case 'email': {
            const mailto = new URL('mailto:');
            mailto.searchParams.append('subject', assetTitle);
            mailto.searchParams.append('body', `${this.elements.emailText.textContent.trim()} ${window.location.href}`);
            window.open(mailto, '_blank');
            break;
        }
        case 'linkedin':
            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`, '_blank');
            break;
        case 'download':
            window.open(url, '_blank');
            break;
        default:
            // do nothing
        }
        this.element.dispatchEvent(new CustomEvent(`${componentName}__share-button-clicked`, { detail: { ...cardEl.dataset, ...ev.currentTarget.dataset } }));
    };

    // ==========
    // EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        // Mobile
        this.elements.mobileTeaserButton.addEventListener('click', this.openMobileContainer);
        this.elements.mobileCloseButton.addEventListener('click', this.closeMobileContainer);

        // Drawer arrow click
        this.elements.drawerArrowLeft.addEventListener('click', () => this.scrollDrawer(-240));
        this.elements.drawerArrowRight.addEventListener('click', () => this.scrollDrawer(240));

        // Card click
        if (this.elements.coverButton) {
            this.elements.coverButton.addEventListener('click', this.handleNextButtonClick);
        }
        this.elements.nextButton.addEventListener('click', this.handleNextButtonClick);
        this.elements.cards.forEach((card) => card.addEventListener('click', this.handleCardClick));

        // Card hover
        this.elements.cards.forEach((card) => card.addEventListener('mouseover', this.handleCardFocus));
        this.elements.cards.forEach((card) => card.addEventListener('focus', this.handleCardFocus));
        this.elements.cards.forEach((card) => card.addEventListener('mouseout', this.handleCardBlur));
        this.elements.cards.forEach((card) => card.addEventListener('blur', this.handleCardBlur));

        // Share buttons
        this.elements.shareButtons.forEach((button) => button.addEventListener('click', this.handleShareButton));
    };

    // ==========
    // INIT
    // ==========
    init = () => {
        this.addEventListeners();
        this.showInitialContent();

        document.addEventListener('binge-gate-submitted', () => {
            this.state.wasFormTriggered = true;
        });

        window.bingeInstance = this; // TEMP: for debug purposes
    };
}

export class OldBingeExperience extends Component {
    elements = {
        // containers
        container: document.querySelector(`.${componentName}__container`),
        mainContainer: document.querySelector(`.${componentName}__main-container`),
        // teasers
        mobileTeaser: document.querySelector(`.${componentName}__mobile-teaser`),
        desktopTeaserOverlay: document.querySelector(`.${componentName}__desktop-teaser-overlay`),
        // modal
        modalWrapper: document.querySelector(`.${componentName}__modal-wrapper`),
        modalMobileClose: document.querySelector(`.${componentName}__modal-mobile-close-button`),
        // menu
        menuContainer: document.querySelector(`.${componentName}__menu-container`),
        menuList: document.querySelector(`.${componentName}__menu-list`),
        mobileMenuExpander: document.querySelector(`.${componentName}__mobile-menu-expander`),
        mobileMenuExpanderIcon: document.querySelector(`.${componentName}__mobile-menu-expander-icon`),
        contactButton: document.querySelector(`.${componentName}__contact-button`),
        // share
        shareEmail: document.querySelector(`.${componentName}__menu-share-item[data-network="email"]`),
        shareLinkedIn: document.querySelector(`.${componentName}__menu-share-item[data-network="linkedin"]`),
        // content
        contentContainer: document.querySelector(`.${componentName}__content-container`),
        nextButton: document.querySelector(`.${componentName}__next-button`),
    };

    classNames = {
        hidden: `${componentName}__hidden`,
        selected: 'selected',
        openLeftContainer: 'open-left-container',
        rotation: 'rotation',
        mobileBingeOpen: 'mobile-binge-open',
        active: `${componentName}__active`,
        gatedContentStyle: `${componentName}__gated-content-style`,
        gatedContentBarrier: `${componentName}__gated-content-barrier`,
    };

    values = {
        contentAssetsViewed: 1,
        gatedContentTriggered: false,
        selectedContentIndex: null,
        pdfOpenFunc: null,
    };

    scrollToContainer() {
        scrollToAnchor(this.elements.container);
    }

    removeSelected() {
        const prevSelected = document.querySelector(`.${this.classNames.selected}`);
        if (prevSelected) {
            prevSelected.classList.remove(this.classNames.selected);
        }

        if (this.values.pdfOpenFunc) {
            document.removeEventListener(
                'webviewerloaded',
                this.values.pdfOpenFunc
            );
        }
    }

    removeAllChildNodes(parent) {
        while (parent.firstChild) {
            parent.removeChild(parent.firstChild);
        }
    }

    openGatedContent() {
        this.values.gatedContentTriggered = true;
        const { conditionalGateId, plexGateModalId } = this.elements.container.dataset;

        if (plexGateModalId) {
            openPlexModal(plexGateModalId);
        } else if (conditionalGateId) {
            const gatedContentName = window.GatedContentObject || 'gcdc';
            const gatedContentObject = window[gatedContentName];
            if (gatedContentObject) {
                const gateDiv = document.createElement('div');
                gateDiv.setAttribute('href', conditionalGateId);
                document.body.appendChild(gateDiv);
                gatedContentObject('loadGates');
            }
        }
    }

    /**
     * @returns true if the window had a hash that matches a list item
     */
    loadInitialContent() {
        let element;
        let hashIndex = null;
        if (window.location.hash) {
            const menuListArray = Array.from(this.elements.menuList.children);
            hashIndex = menuListArray.findIndex(
                (child) => window.location.hash.slice(1) === encodeURI(child.dataset.anchor)
            );

            if (hashIndex !== -1) {
                this.values.selectedContentIndex = hashIndex;
                element = menuListArray[hashIndex];
            } else {
                hashIndex = null;
            }
        }
        if (!element) {
            element = this.elements.menuList.firstChild.nextSibling;
            this.values.selectedContentIndex = 0;
        }
        element.classList.add(this.classNames.selected);
        this.loadContent(element, false);
        return hashIndex !== null;
    }

    loadContent(element, setHash = true) {
        // if (initialContent || !prevSelected.isSameNode(element)) {
        this.removeAllChildNodes(this.elements.contentContainer); // clears content in the contentContainer
        if (element.dataset.type === 'video') {
            const videoElement = document.createElement('video');
            videoElement.setAttribute('controls', true);
            videoElement.setAttribute('autoplay', true);
            videoElement.setAttribute('data-video-id', element.dataset.assetId);
            videoElement.setAttribute('title', element.dataset.assetTitle);
            videoElement.setAttribute('data-title', element.dataset.assetTitle);
            const sourceElement = document.createElement('source');
            sourceElement.setAttribute('src', element.dataset.url);
            videoElement.appendChild(sourceElement);
            this.elements.contentContainer.appendChild(videoElement);
        }
        // /////////////////////////////////////////////////////////SITE
        if (element.dataset.type === 'page') {
            const iFrameElement = document.createElement('iframe');
            iFrameElement.setAttribute('class', 'site-content');
            iFrameElement.setAttribute('src', element.dataset.url);
            iFrameElement.classList.add(bingeExperienceIframeClass);
            this.elements.contentContainer.appendChild(iFrameElement);
        }
        //////////////////////////////////////////////////////PDF
        if (element.dataset.type === 'pdf') {
            this.checkPDFTranslation(element.dataset.url);
        }
        // }

        if (this.isLastItem(element)) {
            this.elements.nextButton.classList.add(this.classNames.hidden);
        } else if (
            !this.isLastItem(element)
            && this.elements.nextButton.classList.contains(this.classNames.hidden)
        ) {
            this.elements.nextButton.classList.remove(this.classNames.hidden);
        }

        if (setHash) {
            window.location.hash = element.dataset.anchor;
        }
    }

    checkPDFTranslation(originalHref) {
        const pageLanguage = document.querySelector('meta[property="language"]')?.content || 'en';
        const pageCountry = document.querySelector('meta[property="country"]')?.content || 'US';
        const pdfLanguage = pageCountry === 'TW' ? 'zc' : pageLanguage;
        if (pdfLanguage !== 'en') {
            const translatedPdf = originalHref.replace(
                /^\/content?(.+-)([a-z]{2})(_[A-Z]{2})?(-.*?\.pdf.*)$/,
                `/content$1${pdfLanguage}$4`
            );
            fetch(translatedPdf, { method: 'HEAD' })
                .then(() => {
                    this.enablePDFViewer(translatedPdf);
                })
                .catch(() => {
                    this.enablePDFViewer(originalHref);
                });
        } else {
            this.enablePDFViewer(originalHref);
        }
    }

    enablePDFViewer(pdfURL) {
        this.elements.contentContainer.innerHTML = `<iframe class="be-pdf-viewer-iframe" src="/etc.clientlibs/rockwell-aem-base/components/content/pdf-viewer/clientlibs/site/resources/viewer.html?file=${pdfURL}" ></iframe>`;
        this.values.pdfOpenFunc = this.openLogic.bind(this);

        document.addEventListener('webviewerloaded', this.values.pdfOpenFunc);
    }

    openLogic() {
        const iframeEle = document.querySelector(
            '.be-pdf-viewer-iframe'
        );
        const { PDFViewerApplication } = iframeEle.contentWindow;
        PDFViewerApplication.initializedPromise.then(() => {
            PDFViewerApplication.eventBus.on('metadataloaded', (event) => {
                PDFViewerApplication.eventBus.on('pagechanging', () => {
                    if (
                        this.elements.container.dataset.type === 'specific'
                        && !this.values.gatedContentTriggered
                        && this.elements.container.dataset.serveBasedOnAsset
                        === 'depth'
                        && Number(this.elements.container.dataset.serveOnAsset)
                        === this.values.selectedContentIndex + 1
                    ) {
                        if (
                            event.source.page
                            >= this.elements.container.dataset.scrollDepth
                        ) {
                            this.openGatedContent();
                        }
                    }
                });
            });
        });
    }

    isLastItem(element) {
        const contentArr = Array.from(this.elements.menuList.children);
        return contentArr.indexOf(element) === contentArr.length - 1;
    }

    shareContent(network) {
        const contentTitle = document.querySelector('.binge-experience__menu-list-item.selected > article > span').textContent;
        const thisPage = window.location.href;
        const message = `Check this out from Rockwell Automation. I thought you'd find it interesting: ${thisPage}`;
        let shareUrl;
        switch (network) {
        case 'email':
            shareUrl = new URL('mailto:');
            shareUrl.searchParams.append('subject', contentTitle);
            shareUrl.searchParams.append('body', message);
            break;
        case 'linkedin':
            shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${thisPage}`;
            break;
        default:
            return;
        }
        window.open(shareUrl, '_blank');
    }

    init() {
        const isInitialHashPresent = this.loadInitialContent();
        // this.values.startContentTimestamp = new Date();

        document.addEventListener('binge-gate-submitted', () => {
            this.values.gatedContentTriggered = true;
        });

        if (
            this.elements.container.dataset.type === 'content'
            && !this.values.gatedContentTriggered
        ) {
            if (
                this.values.contentAssetsViewed
                >= this.elements.container.dataset.count
                && !this.values.gatedContentTriggered
            ) {
                this.openGatedContent();
            }
        }

        if (
            this.elements.container.dataset.type === 'time'
            && !this.values.gatedContentTriggered
        ) {
            window.setTimeout(() => {
                this.openGatedContent();
            }, this.elements.container.dataset.time * 1000);
        }

        if (
            this.elements.container.dataset.type === 'specific'
            && !this.values.gatedContentTriggered
        ) {
            if (
                this.values.selectedContentIndex
                === this.elements.container.dataset.serveOnAsset - 1
            ) {
                if (this.elements.container.dataset.serveBasedOnAsset === 'time') {
                    window.setTimeout(() => {
                        this.openGatedContent();
                    }, this.elements.container.dataset.timeViewed * 1000);
                }
            }
        }

        let element;
        this.elements.menuList.addEventListener('click', (e) => {
            e.preventDefault();
            this.removeSelected();
            if (e.target && e.target.matches('li')) {
                element = e.target;
            } else {
                if (e.target.matches('article')) {
                    element = e.target.parentElement;
                }

                if (e.target.matches('span')) {
                    element = e.target.parentElement.parentElement;
                }
            }
            element.classList.add(this.classNames.selected);
            this.scrollToContainer();

            if (
                document.getElementsByClassName(this.classNames.openLeftContainer).length
                > 0
            ) {
                this.elements.menuContainer.classList.remove(this.classNames.openLeftContainer);
                this.elements.mobileMenuExpanderIcon.classList.remove(this.classNames.rotation);
            }

            const elementIndex = Array.from(
                this.elements.menuList.querySelectorAll('li')
            ).indexOf(element);
            this.values.selectedContentIndex = elementIndex;
            this.values.contentAssetsViewed += 1;
            this.loadContent(element);
            this.values.startContentTimestamp = new Date();

            if (
                this.elements.container.dataset.type === 'content'
                && this.values.contentAssetsViewed
                >= this.elements.container.dataset.count
                && !this.values.gatedContentTriggered
            ) {
                this.openGatedContent();
            }

            if (
                this.elements.container.dataset.type === 'specific'
                && !this.values.gatedContentTriggered
            ) {
                if (
                    this.values.selectedContentIndex
                    === this.elements.container.dataset.serveOnAsset - 1
                ) {
                    if (
                        this.elements.container.dataset.serveBasedOnAsset === 'time'
                    ) {
                        window.setTimeout(() => {
                            this.openGatedContent();
                        }, this.elements.container.dataset.timeViewed * 1000);
                    }
                }
            }
        });

        this.elements.nextButton.addEventListener('click', () => {
            this.scrollToContainer();
            const toBeSelected = document.querySelector(`.${this.classNames.selected}`).nextSibling.nextSibling;
            if (toBeSelected) {
                this.removeSelected();
                toBeSelected.classList.add(this.classNames.selected);
                this.values.selectedContentIndex += 1;
                this.loadContent(toBeSelected);
                this.values.startContentTimestamp = new Date();

                if (
                    this.elements.container.dataset.type === 'content'
                    && this.values.contentAssetsViewed
                    >= this.elements.container.dataset.count
                    && !this.values.gatedContentTriggered
                ) {
                    this.openGatedContent();
                }

                if (
                    this.elements.container.dataset.type === 'specific'
                    && !this.values.gatedContentTriggered
                ) {
                    if (
                        this.values.selectedContentIndex
                        === this.elements.container.dataset.serveOnAsset - 1
                    ) {
                        if (
                            this.elements.container.dataset.serveBasedOnAsset
                            === 'time'
                        ) {
                            window.setTimeout(() => {
                                this.openGatedContent();
                            }, this.elements.container.dataset.timeViewed * 1000);
                        }
                    }
                }
            }
        });

        this.elements.mobileTeaser.addEventListener('click', () => {
            this.elements.mainContainer.classList.add(this.classNames.mobileBingeOpen);
            disableBackgroundScrolling();
            document.body.style.overflow = 'hidden';
            this.elements.container.classList.add(this.classNames.active); // for analytics
        });

        this.elements.modalMobileClose.addEventListener('click', () => {
            this.elements.mainContainer.classList.remove(
                this.classNames.mobileBingeOpen
            );
            enableBackgroundScrolling();
            document.body.style.overflow = 'auto';
            this.elements.container.classList.remove(this.classNames.active);
        });

        this.elements.mobileMenuExpander
            .addEventListener('click', () => {
                if (document.getElementsByClassName(this.classNames.rotation).length > 0) {
                    this.elements.mobileMenuExpanderIcon.classList.remove(this.classNames.rotation);
                } else {
                    this.elements.mobileMenuExpanderIcon.classList.add(this.classNames.rotation);
                }

                if (
                    document.getElementsByClassName(this.classNames.openLeftContainer)
                        .length > 0
                ) {
                    this.elements.menuContainer.classList.remove(this.classNames.openLeftContainer);
                } else {
                    this.elements.menuContainer.classList.add(this.classNames.openLeftContainer);
                }
            });

        if (this.elements.contactButton && this.elements.contactButton.dataset.plexContactModalId) {
            this.elements.contactButton.addEventListener('click', (event) => {
                event.preventDefault();
                openPlexModal(this.elements.contactButton.dataset.plexContactModalId);
            });
        }

        // enable share buttons
        this.elements.shareEmail.addEventListener('click', () => {
            this.shareContent('email');
        });
        this.elements.shareLinkedIn.addEventListener('click', () => {
            this.shareContent('linkedin');
        });

        if (isInitialHashPresent) {
            window.addEventListener('load', () => {
                setTimeout(() => {
                    this.scrollToContainer();
                }, 250);
            });
            if (window.getComputedStyle(this.elements.mobileTeaser).display !== 'none') {
                this.elements.mobileTeaser.click();
            }
        }
    }
}

registerComponent(`.${componentName}__old-design`, OldBingeExperience);
registerComponent(`.${componentName}__new-design`, BingeExperience);
